import {
  Button,
  cn,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Tooltip,
} from '@heroui/react'
import { ArrowUp, ChevronLeft, ChevronRight, RotateCcw, ZoomIn, ZoomOut } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

interface CSVPreviewProps {
  url: string
}

interface CSVRow {
  [key: string]: string
}

export default function CSVPreview({ url }: CSVPreviewProps) {
  const [data, setData] = useState<CSVRow[]>([])
  const [columns, setColumns] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [retryCount, setRetryCount] = useState<number>(0)
  const [fontSize, setFontSize] = useState<number>(14)
  const [showScrollToTop, setShowScrollToTop] = useState<boolean>(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [searchTerm, setSearchTerm] = useState('')

  const containerRef = useRef<HTMLDivElement>(null)
  const scrollTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined)

  // Font size limits
  const MIN_FONT_SIZE = 10
  const MAX_FONT_SIZE = 24
  const DEFAULT_FONT_SIZE = 14

  // Parse CSV content with better handling of quoted fields and edge cases
  const parseCSV = useCallback((csvText: string): { data: CSVRow[]; columns: string[] } => {
    const lines = csvText.trim().split('\n')

    if (lines.length === 0) return { data: [], columns: [] }

    // Parse header row
    const headerRow = lines[0]
    const headers = parseCSVLine(headerRow)

    // Parse data rows
    const dataRows: CSVRow[] = []

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i]

      if (!line.trim()) continue

      const values = parseCSVLine(line)
      const row: CSVRow = {}

      headers.forEach((header, index) => {
        row[header] = values[index] || ''
      })

      dataRows.push(row)
    }

    return { data: dataRows, columns: headers }
  }, [])

  // Helper function to parse CSV line with proper quote handling
  const parseCSVLine = useCallback((line: string): string[] => {
    const result: string[] = []
    let current = ''
    let inQuotes = false
    let i = 0

    while (i < line.length) {
      const char = line[i]

      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          // Escaped quote
          current += '"'
          i += 2
        } else {
          // Toggle quote state
          inQuotes = !inQuotes
          i++
        }
      } else if (char === ',' && !inQuotes) {
        // End of field
        result.push(current.trim())
        current = ''
        i++
      } else {
        current += char
        i++
      }
    }

    // Add the last field
    result.push(current.trim())

    return result
  }, [])

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) return data

    return data.filter(row =>
      Object.values(row).some(value =>
        String(value).toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    )
  }, [data, searchTerm])

  // Load CSV content
  useEffect(() => {
    const fetchCSV = async () => {
      try {
        setIsLoading(true)
        setError(null)

        const response = await fetch(url)

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }

        const csvText = await response.text()
        const { data: csvData, columns: csvColumns } = parseCSV(csvText)

        if (csvColumns.length === 0) {
          throw new Error('Invalid CSV format: No columns found')
        }

        setData(csvData)
        setColumns(csvColumns)
        setIsLoading(false)
      } catch (err) {
        console.error('CSV load error:', err)
        setError('Failed to load CSV file. The file may be corrupted or unavailable.')
        setIsLoading(false)
      }
    }

    fetchCSV()
  }, [url, retryCount, parseCSV])

  // Retry loading CSV
  const handleRetry = useCallback(() => {
    setError(null)
    setIsLoading(true)
    setRetryCount(prev => prev + 1)
  }, [])

  // Font size controls
  const handleZoomIn = useCallback(() => {
    setFontSize(prev => Math.min(prev + 1, MAX_FONT_SIZE))
  }, [])

  const handleZoomOut = useCallback(() => {
    setFontSize(prev => Math.max(prev - 1, MIN_FONT_SIZE))
  }, [])

  const handleReset = useCallback(() => {
    setFontSize(DEFAULT_FONT_SIZE)
  }, [])

  // Scroll to top function
  const scrollToTop = useCallback(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [])

  // Handle scroll to show/hide scroll to top button
  const handleScroll = useCallback(() => {
    if (!containerRef.current) return

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current)
    }

    scrollTimeoutRef.current = setTimeout(() => {
      const container = containerRef.current!
      const scrollTop = container.scrollTop

      setShowScrollToTop(scrollTop > 200)
    }, 80)
  }, [])

  // Pagination
  const totalPages = Math.ceil(filteredData.length / rowsPerPage)
  const startIndex = (currentPage - 1) * rowsPerPage
  const endIndex = startIndex + rowsPerPage
  const currentData = filteredData.slice(startIndex, endIndex)

  // Reset to first page when search changes
  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current)
    }
  }, [])

  if (error) {
    return (
      <div className="max-w-xl text-center text-white">
        <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-red-500/15 text-red-400">
          <svg fill="none" height="28" viewBox="0 0 24 24" width="28">
            <path
              d="M12 9v4m0 4h.01M4.5 19h15a1.5 1.5 0 0 0 1.3-2.25L13.3 4.75a1.5 1.5 0 0 0-2.6 0L3.2 16.75A1.5 1.5 0 0 0 4.5 19Z"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.5"
            />
          </svg>
        </div>
        <h3 className="mb-2 text-xl font-semibold">Unable to preview CSV</h3>
        <p className="text-neutral-200">{error}</p>
        <Button
          className="mt-4 bg-white/15 hover:bg-white/25 text-white"
          size="sm"
          variant="flat"
          onPress={handleRetry}
        >
          Try Again
        </Button>
      </div>
    )
  }

  return (
    <div className="relative w-full h-full flex flex-col items-center justify-center px-8">
      {/* CSV Container */}
      <div
        className={cn(
          'relative flex-1 flex max-w-7xl w-full mx-auto',
          isLoading || (!isLoading && data.length === 0)
            ? 'items-center justify-center'
            : 'items-start justify-start',
        )}
      >
        {isLoading && (
          <div className="text-center">
            <div className="mx-auto mb-4 h-14 w-14 animate-spin rounded-full border-b-2 border-neutral-300" />
            <p className="text-base font-medium text-neutral-100">Loading CSV file...</p>
          </div>
        )}

        {!isLoading && data.length === 0 && (
          <div className="text-center preview-content">
            <div className="mx-auto mb-4 h-16 w-16 flex items-center justify-center rounded-full bg-neutral-500/15 text-neutral-400">
              <svg fill="none" height="28" viewBox="0 0 24 24" width="28">
                <path
                  d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6Z"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.5"
                />
                <path
                  d="M14 2v6h6"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.5"
                />
              </svg>
            </div>
            <h3 className="mb-2 text-xl font-semibold text-neutral-100">CSV File is Empty</h3>
            <p className="text-neutral-300">
              The CSV file was loaded successfully but contains no data rows.
            </p>
          </div>
        )}

        {!isLoading && data.length > 0 && (
          <div className="max-w-7xl w-full mx-auto preview-content">
            <div className="bg-white w-full dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100 shadow-lg border border-neutral-200 dark:border-neutral-700 rounded-lg overflow-hidden">
              {/* CSV Info Header */}
              <div className="bg-neutral-50 dark:bg-neutral-800 px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
                {/* Search and Controls */}
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <input
                      className="w-full px-3 py-2 text-sm border border-neutral-200 dark:border-neutral-600 rounded-md bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Search in data..."
                      type="text"
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-neutral-600 dark:text-neutral-400">
                      Rows per page:
                    </span>
                    <select
                      className="px-2 py-1 text-sm border border-neutral-200 dark:border-neutral-600 rounded bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={rowsPerPage}
                      onChange={e => {
                        setRowsPerPage(Number(e.target.value))
                        setCurrentPage(1)
                      }}
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* CSV Table with contained overflow */}
              <div ref={containerRef}>
                <Table
                  key={fontSize}
                  aria-label="CSV data table"
                  classNames={{
                    wrapper:
                      'min-h-[400px] rounded-none p-0 overflow-auto max-h-[73vh] scroll-smooth relative',
                    thead: 'sticky top-0 z-10',
                    th: 'text-left bg-neutral-50 dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700',
                    td: 'border-b border-neutral-100 dark:border-neutral-700',
                  }}
                  style={{
                    scrollBehavior: 'smooth',
                    WebkitOverflowScrolling: 'touch',
                    overscrollBehavior: 'contain',
                  }}
                  onScroll={handleScroll}
                >
                  <TableHeader columns={columns.map(col => ({ key: col, name: col }))}>
                    {column => (
                      <TableColumn
                        key={column.key}
                        className="font-semibold text-neutral-700 dark:text-neutral-300"
                        style={{ fontSize: `${fontSize}px` }}
                      >
                        {column.name}
                      </TableColumn>
                    )}
                  </TableHeader>
                  <TableBody items={currentData}>
                    {(row: CSVRow) => (
                      <TableRow key={startIndex + currentData.indexOf(row)}>
                        {columns.map(column => (
                          <TableCell
                            key={column}
                            className="max-w-xs truncate"
                            style={{ fontSize: `${fontSize}px` }}
                          >
                            <Tooltip content={row[column] || ''} placement="top">
                              <span className="block truncate">{row[column] || '-'}</span>
                            </Tooltip>
                          </TableCell>
                        ))}
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination Info */}
              {totalPages > 1 && (
                <div className="bg-neutral-50 dark:bg-neutral-800 px-6 py-3 border-t border-neutral-200 dark:border-neutral-700">
                  <div className="flex items-center justify-between text-sm text-neutral-600 dark:text-neutral-400">
                    <span>
                      Showing {startIndex + 1}-{Math.min(endIndex, filteredData.length)} of{' '}
                      {filteredData.length} rows
                    </span>
                    <div className="flex items-center gap-2">
                      <Button
                        isDisabled={currentPage <= 1}
                        size="sm"
                        variant="light"
                        onPress={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      >
                        <ChevronLeft className="h-4 w-4" />
                        Previous
                      </Button>
                      <span className="px-3 py-1 bg-white dark:bg-neutral-700 rounded border border-neutral-200 dark:border-neutral-600">
                        {currentPage} / {totalPages}
                      </span>
                      <Button
                        isDisabled={currentPage >= totalPages}
                        size="sm"
                        variant="light"
                        onPress={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      >
                        Next
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Scroll to Top Button */}
      {showScrollToTop && (
        <Tooltip content="Scroll to top" placement="left">
          <Button
            isIconOnly
            aria-label="Scroll to top"
            className="fixed bottom-24 right-8 bg-black/60 hover:bg-black/80 text-white/90 backdrop-blur-md shadow-lg border border-white/15"
            size="lg"
            variant="flat"
            onPress={scrollToTop}
          >
            <ArrowUp className="h-5 w-5" />
          </Button>
        </Tooltip>
      )}

      {/* Bottom Controls */}
      <div className="pointer-events-none absolute inset-x-0 bottom-[max(16px,env(safe-area-inset-bottom))] flex justify-center bottom-controls z-10">
        <div className="pointer-events-auto flex items-center gap-1 rounded-full border border-white/15 bg-black/60 px-2 py-1 backdrop-blur-md shadow-lg">
          {/* Zoom Controls */}
          <Tooltip content="Zoom out" placement="top">
            <Button
              isIconOnly
              aria-label="Zoom out"
              className="text-white/90 hover:bg-white/10"
              size="sm"
              variant="light"
              onPress={handleZoomOut}
            >
              <ZoomOut className="h-4 w-4" />
            </Button>
          </Tooltip>

          <span className="mx-1 w-12 text-center text-xs text-white/90 tabular-nums">
            {Math.round(fontSize)}px
          </span>

          <Tooltip content="Zoom in" placement="top">
            <Button
              isIconOnly
              aria-label="Zoom in"
              className="text-white/90 hover:bg-white/10"
              size="sm"
              variant="light"
              onPress={handleZoomIn}
            >
              <ZoomIn className="h-4 w-4" />
            </Button>
          </Tooltip>

          <div className="mx-1 h-4 w-px bg-white/20" />

          <Tooltip content="Reset zoom" placement="top">
            <Button
              isIconOnly
              aria-label="Reset zoom"
              className="text-white/90 hover:bg-white/10"
              size="sm"
              variant="light"
              onPress={handleReset}
            >
              <RotateCcw className="h-4 w-4" />
            </Button>
          </Tooltip>
        </div>
      </div>
    </div>
  )
}
